import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth, storage } from '../firebase';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  Avatar,
  Button,
  Grid,
  Divider,
  Modal,
  TextField,
  IconButton,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import BottomNavigationBar from './BottomNavigationBar';

// Helper component for statistics display
const StatItem = ({ count, label, onClick }) => (
  <Box sx={styles.statItem} onClick={onClick}>
    <Typography variant="h6" component="div" sx={styles.statCount}>
      {count}
    </Typography>
    <Typography variant="body2" color="textSecondary" sx={styles.statLabel}>
      {label}
    </Typography>
  </Box>
);

const ProfileScreen = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postCount, setPostCount] = useState(0);
  const [likedCount, setLikedCount] = useState(0); // Holds the calculated total likes
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [caption, setCaption] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Function to fetch and refresh all profile data
  const fetchProfileData = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setLoading(false);
      navigate('/signin');
      return;
    }

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);

        // 1. Fetch ALL Posts by User for comprehensive statistics (Likes & Count)
        const allPostsQuery = query(
          collection(db, 'posts'),
          where('userId', '==', currentUser.uid)
        );
        const allPostsSnapshot = await getDocs(allPostsQuery);
        
        // Set Total Post Count
        setPostCount(allPostsSnapshot.size);
        
        // 2. Calculate Exact Total Likes
        let totalLikes = 0;
        allPostsSnapshot.forEach((doc) => {
          const postData = doc.data();
          // Sum the 'likes' field, defaulting to 0 if undefined
          totalLikes += postData.likes || 0; 
        });
        setLikedCount(totalLikes); 

        // 3. Fetch Actual Posts (First 9) for display grid
        const displayPostsQuery = query(
          collection(db, 'posts'),
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(9)
        );
        const displayPostsSnapshot = await getDocs(displayPostsQuery);
        const fetchedPosts = displayPostsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPosts(fetchedPosts);

      } else {
        console.warn('User document not found in Firestore.');
        setUserData({ firstName: 'User', bio: 'Welcome!', profilePhoto: null, address: 'Unknown' });
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, [navigate]);

  const handleFileChange = (event) => {
    if (event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleUploadPost = async () => {
    if (!selectedFile || !caption.trim()) {
      alert('Please provide a caption and select a photo/video.');
      return;
    }

    setLoading(true);
    const currentUser = auth.currentUser;
    const storageRef = ref(storage, `posts/${currentUser.uid}/${Date.now()}_${selectedFile.name}`);
    const uploadTask = uploadBytesResumable(storageRef, selectedFile);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error('Upload failed:', error);
        setLoading(false);
        setUploadProgress(0);
        alert('Upload failed. Please try again.');
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        await addDoc(collection(db, 'posts'), {
          userId: currentUser.uid,
          caption: caption,
          imageUrl: downloadURL,
          createdAt: serverTimestamp(),
          likes: 0, // Initial likes count
        });
        
        // Reset modal state
        setCaption('');
        setSelectedFile(null);
        setUploadProgress(0);
        setIsModalOpen(false);
        
        // Refresh data to show new post and updated counts
        await fetchProfileData();
        setLoading(false);
      }
    );
  };

  if (loading) {
    return (
      <Box sx={styles.loader}>
        <CircularProgress size={40} sx={{ color: '#C00068' }} />
      </Box>
    );
  }

  const user = userData || {
    firstName: 'Guest',
    bio: 'Profile not loaded.',
    profilePhoto: null,
    address: 'N/A'
  };
  const profilePictureUrl = user.profilePhoto || 'https://via.placeholder.com/150/C00068/FFFFFF?text=P';

  return (
    <Box sx={styles.container}>
      <Container maxWidth="md" sx={{ pb: '60px' }}>
        {/* Profile Header and Settings */}
        <Box sx={styles.header}>
          <Typography variant="h6" sx={styles.usernameText}>
            {user.firstName}
          </Typography>
          <IconButton onClick={() => navigate('/settings')} sx={styles.settingsButton}>
            <SettingsIcon />
          </IconButton>
        </Box>

        {/* Profile Info and Stats */}
        <Box sx={styles.infoSection}>
          <Avatar src={profilePictureUrl} sx={styles.profileAvatar} />

          <Box sx={styles.statsContainer}>
            <StatItem count={postCount} label="Posts" />
            <StatItem count={likedCount} label="Likes" />
            <StatItem count="Edit" label="Profile" onClick={() => navigate('/settings')} />
          </Box>
        </Box>

        {/* Bio Section */}
        <Box sx={styles.bioContainer}>
          <Typography variant="subtitle1" sx={styles.nameText}>
            {user.firstName} {user.lastName || ''}
          </Typography>
          <Typography variant="body2" sx={styles.bioText}>
            {user.bio || 'This user has not set a bio yet.'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {user.address}
          </Typography>
        </Box>

        <Divider sx={styles.divider} />

        {/* Profile Tabs and Actions */}
        <Box sx={styles.tabsContainer}>
          <PhotoLibraryIcon sx={styles.activeTabIcon} />
          <IconButton onClick={() => setIsModalOpen(true)} sx={styles.addPostButton}>
            <AddCircleOutlineIcon sx={{ color: '#C00068', fontSize: 32 }} />
          </IconButton>
        </Box>

        {/* Photo Grid */}
        <Grid container spacing={1}>
          {posts.map((post) => (
            <Grid item xs={4} key={post.id}>
              <Box 
                component="img"
                src={post.imageUrl || 'https://via.placeholder.com/300?text=No+Image'}
                alt={`Post ${post.id}`}
                sx={styles.postImage}
                onClick={() => navigate(`/post/${post.id}`)}
              />
            </Grid>
          ))}
        </Grid>

        {posts.length === 0 && (
          <Box sx={styles.noPosts}>
            <PhotoLibraryIcon sx={{ fontSize: 40, color: '#C00068' }} />
            <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
              No Posts Yet
            </Typography>
            <Button
              variant="contained"
              sx={styles.actionButton}
              onClick={() => setIsModalOpen(true)}
            >
              Share Your First Photo
            </Button>
          </Box>
        )}

      </Container>
      <BottomNavigationBar />

      {/* Post Upload Modal */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box sx={styles.modalContent}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Create New Post</Typography>
            <IconButton onClick={() => setIsModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            label="Caption"
            multiline
            rows={3}
            fullWidth
            margin="normal"
            variant="outlined"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />

          {/* Input for file selection */}
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{ my: 2 }}
          >
            {selectedFile ? selectedFile.name : 'Select Photo/Video'}
            <input
              type="file"
              hidden
              accept="image/*,video/*"
              onChange={handleFileChange}
            />
          </Button>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CircularProgress variant="determinate" value={uploadProgress} size={20} sx={{ mr: 1 }} />
              <Typography variant="body2">{Math.round(uploadProgress)}%</Typography>
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleUploadPost}
            fullWidth
            disabled={loading || !selectedFile || !caption.trim()}
            sx={styles.modalUploadButton}
          >
            {loading ? <CircularProgress size={24} sx={{ color: '#FFF' }} /> : 'Share Post'}
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

// --- Styles ---
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#FFF',
    pt: 2,
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: '10px 16px',
  },
  usernameText: {
    fontWeight: 'bold',
    color: '#333',
  },
  settingsButton: {
    color: '#C00068',
  },
  infoSection: {
    display: 'flex',
    alignItems: 'center',
    p: '16px',
  },
  profileAvatar: {
    width: 80,
    height: 80,
    mr: 3,
    border: '3px solid #C00068',
    flexShrink: 0,
  },
  statsContainer: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  statItem: {
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover .MuiTypography-root': {
      color: '#C00068',
    }
  },
  statCount: {
    fontWeight: 'bold',
    fontSize: '18px',
  },
  statLabel: {
    fontSize: '12px',
    mt: -0.5,
  },
  bioContainer: {
    p: '0 16px 16px 16px',
  },
  nameText: {
    fontWeight: 'bold',
    mb: 0.5,
  },
  bioText: {
    mb: 1,
  },
  divider: {
    my: 1,
  },
  tabsContainer: {
    display: 'flex',
    justifyContent: 'center',
    borderBottom: '1px solid #eee',
    mb: 1,
    position: 'relative',
  },
  activeTabIcon: {
    fontSize: 28,
    p: 1,
    color: '#C00068',
    borderBottom: '2px solid #C00068',
  },
  addPostButton: {
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    p: '4px',
  },
  postImage: {
    width: '100%',
    height: 120,
    objectFit: 'cover',
    display: 'block',
    cursor: 'pointer',
  },
  noPosts: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    textAlign: 'center',
  },
  actionButton: {
    mt: 3,
    textTransform: 'none',
    fontWeight: 700,
    background: `linear-gradient(45deg, #B200BB, #FF3B30)`,
    backgroundColor: '#C00068',
    '&:hover': {
      backgroundColor: '#9A0054',
    }
  },
  modalContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 400 },
    bgcolor: 'background.paper',
    borderRadius: '10px',
    boxShadow: 24,
    p: 4,
  },
  modalUploadButton: {
    backgroundColor: '#C00068',
    '&:hover': {
      backgroundColor: '#9A0054',
    }
  }
};

export default ProfileScreen;